import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Eye, PackagePlus } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIdentities, useMe } from "../api/queries.ts";
import { readPurchaseAs, readViewAs } from "../lib/identity.ts";

/**
 * 「你正在按什么价看」提示条。
 *
 * 🔴 **换了价格体系就必须说出来。**
 *    同一件商品在这个平台上有三个完全不同的价：供货价 / 进货价 / 零售价。
 *    不说的话商家会以为平台改价了，或者更糟 —— 拿这个数去跟客户报价。
 *
 * ⚠️ 早期做过一版"前台放个进货开关"，页面上同时出现两个价，
 *    结果是「切到进货反而变贵」这种诡异现象（拿平台零售价跟进货价并排比，
 *    两个数根本不该放一起）。所以这里**只说明当前是哪一种**，不做对比。
 *
 * ⚠️ `wide` 给网页版用：条子本身通栏（跟顶栏一样宽才像个系统提示），
 *    但文字要跟页面内容对齐到同一条左边线上。手机版不传，行为完全不变。
 */
function Bar({
  tone,
  icon,
  wide,
  children,
}: {
  tone: string;
  icon: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn("border-b", tone)}>
      <div
        className={cn(
          "flex items-start gap-2 px-3 py-2 text-[12px] leading-relaxed",
          wide && "mx-auto max-w-[1280px] px-8 py-2.5 text-[13px]",
        )}
      >
        {icon}
        <span className="min-w-0 flex-1">{children}</span>
      </div>
    </div>
  );
}

export function PurchaseBanner({ wide }: { wide?: boolean } = {}) {
  const { data: me } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  /**
   * ⚠️ 直接读存起来的身份，不自己推断 —— 取数入口用的就是这个值，
   *    两边必须是同一个来源，否则会出现"提示说在进货、价格却是零售价"。
   */
  const orgId = readPurchaseAs();
  if (!orgId) return null;

  const org = identities?.orgs.find((o) => o.id === orgId);
  if (!org) return null;

  const view = readViewAs();

  /**
   * ── 看自己的店（选中了店铺名）──
   *
   * 🔴 **只读预览。** 这是**客户**看到的价，按它下单会生成一张"自己卖给自己"
   *    的单，而且跟服务端按真实身份算出的价对不上，会被拦下。
   *    与其让他点了才报错，不如先说清楚。
   */
  if (view === "shop") {
    return (
      <Bar
        wide={wide}
        tone="border-sky-200/60 bg-sky-50 text-sky-900"
        icon={<Eye className="mt-0.5 size-4 shrink-0" />}
      >
        这是<strong>客户在「{org.name}」看到的价</strong>，只能看不能买。
        想按自己的进货价买，切回
        <Link to="/me" className="mx-1 whitespace-nowrap underline underline-offset-2">
          「我自己」
        </Link>
        。
      </Bar>
    );
  }

  /**
   * ── 我自己（进货）──
   *
   * 🔴 **有上级的人看到的就该是上级给他的价，不是平台指导价。**
   *    平台指导价是给**自己注册、没有上级**的陌生人看的最贵一档。
   */
  return (
    <Bar
      wide={wide}
      tone="border-amber-200/60 bg-amber-50 text-amber-800"
      icon={<PackagePlus className="mt-0.5 size-4 shrink-0" />}
    >
      下面是<strong>你的进货价</strong>（上级给「{org.name}」的价）。
      {/*
        ⚠️ 要给一条"怎么看客户价"的路 —— 不给的话他不知道还能切，
           而切换器在「我的」（网页版在账户中心左栏），不点进去看不见。
      */}
      想看客户在你店里看到多少，去
      <Link to="/me" className="mx-1 whitespace-nowrap underline underline-offset-2">
        切到店铺
      </Link>
      。
    </Bar>
  );
}
